import { useState, useEffect, useMemo, useRef } from 'react';
import { MdPeople, MdPersonAdd, MdMoreVert, MdEdit, MdDelete, MdShare, MdMessage, MdAdd, MdClose, MdSchedule, MdAttachMoney, MdWork, MdCheckCircle, MdNotifications } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
// import DatePicker from 'react-tailwindcss-datepicker'; // Not needed anymore
import api, { getPaymentReminderSettingsForFriend, updatePaymentReminderSettingsForFriend } from '../../services/api';
import { getUser } from '../../services/auth';
import { t } from '../../utils/i18n';
import { useToast } from '../../components/ToastContainer';
import ToastContainer from '../../components/ToastContainer';
import ConfirmDialog from '../../components/ConfirmDialog';
import DropdownOrBottomSheet from '../../components/DropdownOrBottomSheet';
import { useIsMobile } from '../../hooks/useIsMobile';

export default function FriendsPage() {
  const isMobile = useIsMobile();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, friendId: null });
  const { showToast, removeToast, toasts } = useToast();
  const [addMethod, setAddMethod] = useState('email'); // 'email' or 'manual'
  const [friendEmail, setFriendEmail] = useState('');
  const [relationshipType, setRelationshipType] = useState('babysitter'); // 'babysitter' or 'family'
  const [manualFriend, setManualFriend] = useState({
    name: '',
    email: '',
    phone: '',
    relationshipType: 'babysitter',
    flexibleRates: [] // Array of { days: [0-6], startTime: "09:00", endTime: "17:00", rate: 800 }
  });
  const [addingFriend, setAddingFriend] = useState(false);
  const [editingPaymentSchedule, setEditingPaymentSchedule] = useState(null); // stores friend object
  const [paymentSchedule, setPaymentSchedule] = useState({
    scheduleType: 'monthly', // 'monthly' | 'weekly' | 'weeklyInterval'
    days: [], // For monthly: ['first', 'last', '15', etc.], For weekly: ['monday', 'tuesday', etc.]
    time: '09:00', // Time picker value (HH:mm)
    // For weekly interval (e.g. every 2nd Friday)
    intervalWeeks: 2,
    weekday: 'friday',
    anchorDate: null, // YYYY-MM-DD (optional; keeps alignment when editing)
  });
  const [editingFullTimeWork, setEditingFullTimeWork] = useState(null); // stores friend object
  const [fullTimeWork, setFullTimeWork] = useState({
    enabled: false,
    startTime: '09:00',
    endTime: '17:00',
    days: [] // Array of day numbers: [1,2,3,4,5] for Monday-Friday
  });
  const [openMenuId, setOpenMenuId] = useState(null); // friendRowKey(friend) for which menu is open
  const [invitingSharedFriendId, setInvitingSharedFriendId] = useState(null);
  const [shareConfirm, setShareConfirm] = useState({ isOpen: false, friendId: null });
  const [shareSettings, setShareSettings] = useState({
    calendar: true,
    payments: false,
    babysitters: false,
    calendarWrite: false
  });
  const [inviteFriendDialog, setInviteFriendDialog] = useState({ isOpen: false, friend: null });
  const [editingFriend, setEditingFriend] = useState(null); // friend object being edited
  const [editingFriendRates, setEditingFriendRates] = useState(false); // Show rates editor
  const [editingReminders, setEditingReminders] = useState(null); // friend for whom we edit reminders
  const [reminderSettings, setReminderSettings] = useState({
    reminder7d: true,
    reminder3d: true,
    reminder0d: true,
    reminderPlus3d: true,
    emailEnabled: true,
    notificationEnabled: true,
  });
  const [reminderSettingsLoading, setReminderSettingsLoading] = useState(false);
  const [editFriendForm, setEditFriendForm] = useState({
    name: '',
    email: '',
    phone: '',
    hourlyRate: '',
    flexibleRates: []
  });
  const menuRefs = useRef({}); // Store refs for each friend's menu trigger
  const user = getUser();
  const friendRowKey = (friend) => (friend?.isShared ? `shared-${friend.id}` : String(friend?.id ?? ''));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const familyId = searchParams.get('familyId');

  const friendStats = useMemo(() => {
    const total = friends.length;
    const babysitters = friends.filter(f => f.relationshipType === 'babysitter').length;
    const families = friends.filter(f => f.relationshipType === 'family').length;
    const noAccount = friends.filter(f => !f.hasAccount).length;
    return { total, babysitters, families, noAccount };
  }, [friends]);

  const weekdayToLabel = (weekday) => {
    const map = {
      monday: t('calendar.monday') || 'Ponedeljak',
      tuesday: t('calendar.tuesday') || 'Utorak',
      wednesday: t('calendar.wednesday') || 'Sreda',
      thursday: t('calendar.thursday') || 'Četvrtak',
      friday: t('calendar.friday') || 'Petak',
      saturday: t('calendar.saturday') || 'Subota',
      sunday: t('calendar.sunday') || 'Nedelja',
    };
    return map[weekday] || weekday;
  };

  const weekdayToFullLabel = (weekday) => {
    const map = {
      monday: t('friends.weekdayMondayFull') || 'ponedeljak',
      tuesday: t('friends.weekdayTuesdayFull') || 'utorak',
      wednesday: t('friends.weekdayWednesdayFull') || 'sreda',
      thursday: t('friends.weekdayThursdayFull') || 'četvrtak',
      friday: t('friends.weekdayFridayFull') || 'petak',
      saturday: t('friends.weekdaySaturdayFull') || 'subota',
      sunday: t('friends.weekdaySundayFull') || 'nedelja',
    };
    return map[weekday] || weekday;
  };

  const getPaymentScheduleLabel = (friend) => {
    if (friend.relationshipType !== 'babysitter' || !friend.paymentSchedule) return null;
    const schedule = friend.paymentSchedule;
    if (schedule.scheduleType === 'everyNth') {
      return `${t('friends.everyNthDay') || 'Svaki'} ${schedule.everyNthDay || schedule.dayOfMonth || 1}. ${t('friends.dayNumber') || 'dan'}`;
    }
    const scheduleDays = Array.isArray(schedule.days) ? schedule.days : [];
    const isWeeklyDays = scheduleDays.some(d => ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].includes(d));
    if (schedule.repeatType === 'weekly' || schedule.scheduleType === 'weekly' || isWeeklyDays) {
      const interval = parseInt(schedule.intervalWeeks, 10);
      const normalizedInterval = Number.isFinite(interval) && interval > 1 ? interval : 1;
      const labels = scheduleDays.filter(d => ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].includes(d)).map(d => weekdayToLabel(d));
      const dayLabel = labels.length ? labels.join(', ') : (schedule.date ? weekdayToLabel(['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date(schedule.date).getDay()]) : (t('friends.weekly') || 'Nedeljno'));
      if (normalizedInterval > 1) return `Plaćanje: svake ${normalizedInterval}. nedelje (${dayLabel})`;
      return `Plaćanje: ${dayLabel}`;
    }
    if (scheduleDays.length > 0) {
      const dayLabels = scheduleDays.map(d => {
        if (d === 'first') return t('friends.firstDay') || 'Prvi dan';
        if (d === 'fifteenth') return t('friends.fifteenthDay') || 'polovina meseca';
        if (d === 'last') return t('friends.lastDay') || 'poslednji dan meseca';
        if (typeof d === 'string' && !Number.isNaN(parseInt(d, 10))) return `${d}.`;
        return String(d);
      });
      const sorted = [...dayLabels].sort((a, b) => (a.includes('polovina') ? -1 : b.includes('polovina') ? 1 : a.includes('poslednji') ? 1 : b.includes('poslednji') ? -1 : 0));
      return `Plaćanje: ${sorted.join(' i ')}`;
    }
    return schedule.scheduleType === 'everyNth' ? `${t('friends.everyNthDay') || 'Svaki'} ${schedule.everyNthDay || schedule.dayOfMonth || 1}. ${t('friends.dayNumber') || 'dan'}` : `Plaćanje: ${schedule.dayOfMonth || 1}. dan`;
  };

  const getFullTimeWorkLabel = (friend) => {
    if (friend.relationshipType !== 'babysitter' || !friend.fullTimeWork?.enabled) return null;
    const ft = friend.fullTimeWork;
    if (ft.days && ft.days.length === 5 && ft.days.every(d => d >= 1 && d <= 5) && [...ft.days].sort().join(',') === '1,2,3,4,5') {
      return `Radno vreme: Pon-Pet: ${ft.startTime} - ${ft.endTime}`;
    }
    const dayNames = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
    const dayLabels = (ft.days || []).map(d => dayNames[d] || d).join(', ');
    return dayLabels ? `${dayLabels}: ${ft.startTime} - ${ft.endTime}` : `${ft.startTime} - ${ft.endTime}`;
  };

  const formatLocalYMD = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const normalizeAnchorToWeekday = (dateStr, weekday) => {
    if (!dateStr || typeof dateStr !== 'string' || !weekday) return dateStr;
    const map = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    const target = map[weekday];
    if (target === undefined) return dateStr;
    const [yy, mm, dd] = dateStr.split('-').map((n) => parseInt(n, 10));
    if (!yy || !mm || !dd) return dateStr;
    // Use noon local time to avoid DST/UTC boundary issues.
    const dt = new Date(yy, mm - 1, dd, 12, 0, 0, 0);
    if (Number.isNaN(dt.getTime())) return dateStr;
    // If misaligned (e.g., Fri saved as Thu due to UTC conversion), walk forward to the correct weekday.
    for (let i = 0; i < 7; i++) {
      if (dt.getDay() === target) return formatLocalYMD(dt);
      dt.setDate(dt.getDate() + 1);
    }
    return dateStr;
  };

  const nextDateForWeekday = (weekday, base = new Date()) => {
    const map = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    const target = map[weekday];
    if (target === undefined) return null;
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0, 0);
    const diff = (target - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + diff);
    // IMPORTANT: use local YYYY-MM-DD (avoid timezone shifting like Fri -> Thu in CET when using toISOString).
    return formatLocalYMD(d);
  };

  // Removed datePickerValue useMemo - using simple input instead

  useEffect(() => {
    loadFriends();
  }, []);

  // Auto-select babysitter filter when familyId is in URL
  useEffect(() => {
    if (familyId) {
      setRelationshipType('babysitter');
    }
  }, [familyId]);

  // If the user scrolls/resizes while the menu is open, close it (avoids stale positioning).
  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [openMenuId]);

  // Load reminder settings when opening reminder modal for a friend
  useEffect(() => {
    if (!editingReminders) return;
    const friendId = editingReminders.friendshipId || editingReminders.id;
    setReminderSettingsLoading(true);
    getPaymentReminderSettingsForFriend(friendId)
      .then((data) => {
        setReminderSettings({
          reminder7d: data.reminder7d !== false,
          reminder3d: data.reminder3d !== false,
          reminder0d: data.reminder0d !== false,
          reminderPlus3d: data.reminderPlus3d !== false,
          emailEnabled: data.emailEnabled !== false,
          notificationEnabled: data.notificationEnabled !== false,
        });
      })
      .catch(() => {
        setReminderSettings({
          reminder7d: true,
          reminder3d: true,
          reminder0d: true,
          reminderPlus3d: true,
          emailEnabled: true,
          notificationEnabled: true,
        });
      })
      .finally(() => setReminderSettingsLoading(false));
  }, [editingReminders]);

  const loadFriends = async () => {
    try {
      setLoading(true);
      const response = await api.get('/friends').catch((err) => {
        console.error('Error loading friends:', err);
        console.error('Error details:', err.response?.data || err.message);
        const baseMsg = t('friends.loadError') || 'Greška pri učitavanju prijatelja.';
        const detail = err.response?.data?.message || err.message;
        showToast(detail ? `${baseMsg} (${detail})` : baseMsg, 'error');
        return { data: [] };
      });
      if (import.meta.env.DEV) console.log('Friends loaded:', response.data);
      const friendsData = Array.isArray(response.data) ? response.data : [];
      if (import.meta.env.DEV) console.log(`Loaded ${friendsData.length} friends:`, friendsData.map(f => ({ id: f.id, name: f.name, email: f.email, hasAccount: f.hasAccount })));
      setFriends(friendsData);
    } catch (error) {
      console.error('Error loading friends:', error);
      const baseMsg = t('friends.loadError') || 'Greška pri učitavanju prijatelja.';
      const detail = error.response?.data?.message || error.message;
      showToast(detail ? `${baseMsg} (${detail})` : baseMsg, 'error');
      setFriends([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFriend = async (e) => {
    e.preventDefault();
    if (!friendEmail) return;

    setAddingFriend(true);
    try {
      // Send friend request with email notification
      const response = await api.post('/friends/invite', { 
        email: friendEmail,
        relationshipType: relationshipType
      });
      
      if (response?.data?.success) {
        showToast(t('friends.inviteSent') || 'Pozivnica poslata! Prijatelj će dobiti email za prihvatanje.', 'success');
        setFriendEmail('');
        setRelationshipType('babysitter');
        setShowAddFriend(false);
      }
    } catch (error) {
      const msg = error.response?.data?.message || '';
      const isUserNotFound = error.response?.status === 404 && (msg.toLowerCase().includes('user not found') || msg.toLowerCase().includes('nije na'));
      if (isUserNotFound) {
        // Ponudi slanje pozivnice na Nannity umesto greške
        setInviteFriendDialog({
          isOpen: true,
          friend: { email: friendEmail, name: friendEmail, relationshipType, fromAddByEmail: true }
        });
      } else if (error.response?.status === 404 || error.response?.status === 400) {
        showToast(msg || t('friends.addError') || 'Greška pri slanju pozivnice: ' + error.message, 'error');
      } else {
        showToast(t('friends.addError') || 'Greška pri slanju pozivnice: ' + error.message, 'error');
      }
    } finally {
      setAddingFriend(false);
    }
  };

  const handleInviteSharedBabysitter = async (friend) => {
    if (!friend?.email) {
      showToast(t('friends.noEmailForInvite') || 'Nema email adrese za slanje poziva.', 'error');
      return;
    }
    setInvitingSharedFriendId(friend.id);
    try {
      const response = await api.post('/friends/invite', {
        email: friend.email,
        relationshipType: 'babysitter'
      });
      if (response?.data?.success) {
        showToast(t('friends.inviteSent') || 'Pozivnica poslata! Prijatelj će dobiti email za prihvatanje.', 'success');
        loadFriends();
      }
    } catch (error) {
      const msg = error.response?.data?.message || '';
      const isUserNotFound = error.response?.status === 404 && (msg.toLowerCase().includes('user not found') || msg.toLowerCase().includes('nije na'));
      if (isUserNotFound) {
        setInviteFriendDialog({
          isOpen: true,
          friend: { email: friend.email, name: friend.name || friend.email, relationshipType: 'babysitter', fromAddByEmail: true }
        });
      } else {
        showToast(msg || t('friends.addError') || 'Greška pri slanju pozivnice.', 'error');
      }
    } finally {
      setInvitingSharedFriendId(null);
      setOpenMenuId(null);
    }
  };

  const confirmRemoveFriend = async () => {
    const friendId = deleteConfirm.friendId;
    if (!friendId) return;

    try {
      await api.delete(`/friends/${friendId}`);
      setFriends(friends.filter(f => f.id !== friendId));
      showToast(t('friends.removed') || 'Prijatelj je uspešno uklonjen.', 'success');
      setDeleteConfirm({ isOpen: false, friendId: null });
    } catch (error) {
      showToast(t('friends.removeError') || 'Greška pri uklanjanju prijatelja: ' + error.message, 'error');
    }
  };

  const handleShareCalendar = (friendId) => {
    const friend = friends.find(f => f.id === friendId);
    if (!friend || !friend.hasAccount) {
      showToast(t('friends.noAccountShare') || 'Ne možete podeliti kalendar sa prijateljem koji nema nalog.', 'info');
      return;
    }
    // Pre-fill with current share state so user sees what is already shared
    const current = friend.shareSettings || {};
    setShareSettings({
      calendar: current.calendar || friend.calendarShared || false,
      payments: current.payments || false,
      babysitters: current.babysitters || false,
      calendarWrite: current.calendarWrite || false
    });
    setShareConfirm({ isOpen: true, friendId });
  };

  const hasAnyShared = (friend) => {
    if (!friend) return false;
    const s = friend.shareSettings || {};
    return (s.calendar || friend.calendarShared) || s.payments || s.babysitters;
  };

  const getSharedLabel = (friend) => {
    const s = friend.shareSettings || {};
    const cal = s.calendar || friend.calendarShared;
    const parts = [];
    if (cal) {
      parts.push(s.calendarWrite ? 'Kalendar + izmena' : (t('friends.shareCalendar') || 'Kalendar'));
    }
    if (s.payments) parts.push(t('friends.sharePayments') || 'Plaćanja');
    if (s.babysitters) parts.push(t('friends.shareBabysitters') || 'Bebisiterke');
    return parts.length ? parts.join(', ') : '';
  };

  const handleUnshareAll = async () => {
    const friendId = shareConfirm.friendId;
    if (!friendId) return;
    try {
      await api.post(`/friends/${friendId}/share-calendar`, {
        shareCalendar: false,
        sharePayments: false,
        shareBabysitters: false,
        shareCalendarWrite: false
      });
      showToast(t('friends.unshareSuccess') || 'Deljenje uklonjeno.', 'success');
      setShareConfirm({ isOpen: false, friendId: null });
      setShareSettings({ calendar: true, payments: false, babysitters: false, calendarWrite: false });
      loadFriends();
    } catch (error) {
      showToast(t('friends.unshareError') || 'Greška pri uklanjanju deljenja: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleInviteFriend = async () => {
    const friend = inviteFriendDialog.friend;
    if (!friend || !friend.email) return;

    try {
      const response = await api.post('/friends/invite-manual', {
        email: friend.email,
        name: friend.name || friend.email.split('@')[0] || '',
        relationshipType: friend.relationshipType || 'babysitter'
      });

      if (response?.data?.success) {
        showToast(t('friends.inviteSent') || 'Pozivnica poslata! Prijatelj će dobiti email sa linkom za registraciju.', 'success');
        if (friend.fromAddByEmail) {
          setFriendEmail('');
          setShowAddFriend(false);
          try {
            await api.post('/friends/manual', {
              name: friend.name || friend.email.split('@')[0] || t('friends.invitedFriend') || 'Pozvani prijatelj',
              email: friend.email,
              relationshipType: friend.relationshipType || 'babysitter'
            });
            loadFriends();
          } catch (_) {
            // list refresh optional
          }
        }
        setInviteFriendDialog({ isOpen: false, friend: null });
      }
    } catch (error) {
      console.error('Error inviting friend:', error);
      showToast(t('friends.inviteError') || 'Greška pri slanju pozivnice: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const confirmShareCalendar = async () => {
    const friendId = shareConfirm.friendId;
    if (!friendId) return;

    // Check if at least one option is selected
    if (!shareSettings.calendar && !shareSettings.payments && !shareSettings.babysitters) {
      showToast(t('friends.selectShareOption') || 'Izaberite bar jednu opciju za deljenje.', 'error');
      return;
    }

    try {
      await api.post(`/friends/${friendId}/share-calendar`, {
        shareCalendar: shareSettings.calendar,
        sharePayments: shareSettings.payments,
        shareBabysitters: shareSettings.babysitters,
        shareCalendarWrite: shareSettings.calendar && shareSettings.calendarWrite
      });
      showToast(t('friends.calendarShared') || 'Uspešno podeljeno sa prijateljem.', 'success');
      setShareConfirm({ isOpen: false, friendId: null });
      setShareSettings({ calendar: true, payments: false, babysitters: false, calendarWrite: false }); // Reset to defaults
      // Reload friends to update UI
      loadFriends();
    } catch (error) {
      showToast(t('friends.shareError') || 'Greška pri deljenju: ' + error.message, 'error');
      setShareConfirm({ isOpen: false, friendId: null });
    }
  };

  const handleEditFriend = (friend) => {
    setEditingFriend(friend);
    setEditFriendForm({
      name: friend.name || '',
      email: friend.email || '',
      phone: friend.phone || '',
      hourlyRate: friend.hourlyRate || '',
      flexibleRates: friend.flexibleRates || []
    });
    setEditingFriendRates(false);
    setOpenMenuId(null);
  };

  const handleSaveEditFriend = async () => {
    if (!editingFriend) return;

    try {
      // Use friendshipId (the ID from friendships table) for the API call
      const targetFriendshipId = editingFriend.friendshipId;
      if (!targetFriendshipId) {
        showToast(t('friends.updateError') || 'Nedostaje identifikator prijatelja za ažuriranje.', 'error');
        return;
      }

      // Update friend via API
      const updateData = {};
      
      // Only include name if it's not empty
      if (editFriendForm.name && editFriendForm.name.trim()) {
        updateData.name = editFriendForm.name.trim();
      }
      
      // Only include email if it's not empty
      if (editFriendForm.email && editFriendForm.email.trim()) {
        updateData.email = editFriendForm.email.trim();
      }
      
      // Only include phone if it's not empty
      if (editFriendForm.phone && editFriendForm.phone.trim()) {
        updateData.phone = editFriendForm.phone.trim();
      }
      
      // Only include hourlyRate and flexibleRates if we're editing rates
      if (editingFriendRates) {
        // Convert hourlyRate to number, or skip if empty
        if (editFriendForm.hourlyRate && editFriendForm.hourlyRate !== '') {
          const hourlyRateNum = parseFloat(editFriendForm.hourlyRate);
          if (!isNaN(hourlyRateNum) && hourlyRateNum >= 0) {
            updateData.hourlyRate = hourlyRateNum;
          }
        }
        if (editFriendForm.flexibleRates && Array.isArray(editFriendForm.flexibleRates)) {
          updateData.flexibleRates = editFriendForm.flexibleRates;
        }
      }
      
      await api.put(`/friends/${targetFriendshipId}`, updateData);
      setFriends(friends.map(f => 
        f.friendshipId === targetFriendshipId 
          ? { ...f, ...updateData }
          : f
      ));
      showToast(t('friends.friendUpdated') || 'Prijatelj je ažuriran.', 'success');
      setEditingFriend(null);
      setEditingFriendRates(false);
      setEditFriendForm({ name: '', email: '', phone: '', hourlyRate: '', flexibleRates: [] });
    } catch (error) {
      console.error('Error updating friend:', error);
      showToast(t('friends.updateError') || 'Greška pri ažuriranju prijatelja.', 'error');
    }
  };

  const handleSendMessage = (friendId) => {
    // Navigate to conversations page and create/select conversation with this friend
    navigate(`/dashboard/conversations?friendId=${friendId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full overflow-y-auto overflow-x-hidden relative">
        <div className="relative z-10 p-4 lg:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
              <MdPeople className="text-2xl text-primary-600 dark:text-primary-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('friends.title') || 'Prijatelji'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('friends.subtitle') || 'Upravljaj vezama, deljenjem i komunikacijom na jednom mestu.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAddFriend(!showAddFriend)}
            className="btn-primary flex items-center justify-center gap-2 w-full lg:w-auto"
          >
            <MdPersonAdd />
            {t('friends.addFriend') || 'Dodaj prijatelja'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="surface-card p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('friends.total') || 'Ukupno'}
            </p>
            <p className="text-2xl font-bold text-primary-600 mt-2">
              {friendStats.total}
            </p>
          </div>
          <div className="surface-card p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('friends.babysitters') || 'Dadilje'}
            </p>
            <p className="text-2xl font-bold text-emerald-600 mt-2">
              {friendStats.babysitters}
            </p>
          </div>
          <div className="surface-card p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('friends.families') || 'Porodice'}
            </p>
            <p className="text-2xl font-bold text-primary-600 mt-2">
              {friendStats.families}
            </p>
          </div>
          <div className="surface-card p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('friends.noAccount') || 'Bez naloga'}
            </p>
            <p className="text-2xl font-bold text-amber-600 mt-2">
              {friendStats.noAccount}
            </p>
          </div>
        </div>

      {/* Add Friend Form */}
      {showAddFriend && (
        <div className="surface-card p-4">
          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setAddMethod('email')}
              className={`flex-1 py-2 px-4 rounded-xl text-sm font-semibold ${
                addMethod === 'email'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {t('friends.addByEmail') || 'Dodaj po emailu'}
            </button>
            <button
              type="button"
              onClick={() => setAddMethod('manual')}
              className={`flex-1 py-2 px-4 rounded-xl text-sm font-semibold ${
                addMethod === 'manual'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {t('friends.addManually') || 'Ručni unos'}
            </button>
          </div>

          {addMethod === 'email' ? (
            <form onSubmit={handleAddFriend} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('friends.friendEmail') || 'Email prijatelja'}
                </label>
                <input
                  type="email"
                  value={friendEmail}
                  onChange={(e) => setFriendEmail(e.target.value)}
                  placeholder={t('friends.emailPlaceholder') || 'Unesite email prijatelja'}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('friends.relationshipType') || 'Tip veze'}
                </label>
                <select
                  value={relationshipType}
                  onChange={(e) => setRelationshipType(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="babysitter">
                    {user?.role === 'babysitter' 
                      ? (t('friends.family') || 'Porodica sa kojom sarađujem')
                      : (t('friends.babysitter') || 'Bebisiterka')
                    }
                  </option>
                  <option value="family">
                    {user?.role === 'babysitter'
                      ? (t('friends.babysitter') || 'Bebisiterka')
                      : (t('friends.family') || 'Porodica')
                    }
                  </option>
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={addingFriend}
                  className="btn-primary flex-1"
                >
                  {addingFriend ? t('friends.adding') || 'Dodavanje...' : t('friends.add') || 'Dodaj'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddFriend(false);
                    setFriendEmail('');
                    setRelationshipType('babysitter');
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setAddingFriend(true);
              try {
                if (import.meta.env.DEV) console.log('Adding friend:', manualFriend);
                // Send manual friend addition to backend
                const response = await api.post('/friends/manual', {
                  name: manualFriend.name,
                  email: manualFriend.email,
                  phone: manualFriend.phone,
                  relationshipType: manualFriend.relationshipType,
                  flexibleRates: manualFriend.flexibleRates
                });
                
                if (import.meta.env.DEV) console.log('Add friend response:', response.data);
                
                if (response?.data) {
                  // Reload friends list
                  await loadFriends();
                  showToast(t('friends.added') || 'Prijatelj je dodat.', 'success');
                  setShowAddFriend(false);
                  setManualFriend({
                    name: '',
                    email: '',
                    phone: '',
                    relationshipType: 'babysitter',
                    flexibleRates: []
                  });
                } else {
                  showToast(t('friends.addError') || 'Greška pri dodavanju prijatelja.', 'error');
                }
              } catch (error) {
                console.error('Error adding friend:', error);
                console.error('Error response:', error.response?.data);
                showToast(t('friends.addError') || 'Greška pri dodavanju prijatelja: ' + (error.response?.data?.message || error.message || 'Unknown error'), 'error');
              } finally {
                setAddingFriend(false);
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('profile.firstName') || 'Ime'} / {t('friends.name') || 'Naziv'}
                </label>
                <input
                  type="text"
                  value={manualFriend.name}
                  onChange={(e) => setManualFriend({ ...manualFriend, name: e.target.value })}
                  placeholder={t('friends.namePlaceholder') || 'Unesite ime ili naziv'}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('friends.friendEmail') || 'Email prijatelja'}
                </label>
                <input
                  type="email"
                  value={manualFriend.email}
                  onChange={(e) => setManualFriend({ ...manualFriend, email: e.target.value })}
                  placeholder={t('friends.emailPlaceholder') || 'Unesite email prijatelja'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('profile.phone') || 'Telefon'}
                </label>
                <input
                  type="tel"
                  value={manualFriend.phone}
                  onChange={(e) => setManualFriend({ ...manualFriend, phone: e.target.value })}
                  placeholder={t('friends.phonePlaceholder') || 'Unesite telefon'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('friends.relationshipType') || 'Tip veze'}
                </label>
                <select
                  value={manualFriend.relationshipType}
                  onChange={(e) => setManualFriend({ ...manualFriend, relationshipType: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="babysitter">
                    {user?.role === 'babysitter' 
                      ? (t('friends.family') || 'Porodica sa kojom sarađujem')
                      : (t('friends.babysitter') || 'Bebisiterka')
                    }
                  </option>
                  <option value="family">
                    {user?.role === 'babysitter'
                      ? (t('friends.babysitter') || 'Bebisiterka')
                      : (t('friends.family') || 'Porodica')
                    }
                  </option>
                </select>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('calendar.flexibleRates') || 'Fleksibilne cene (RSD/h)'}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setManualFriend({
                        ...manualFriend,
                        flexibleRates: [
                          ...manualFriend.flexibleRates,
                          {
                            days: [1, 2, 3, 4, 5], // Monday-Friday by default (1=Pon, 2=Uto, 3=Sre, 4=Čet, 5=Pet)
                            startTime: '09:00',
                            endTime: '17:00',
                            rate: 800
                          }
                        ]
                      });
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
                  >
                    <MdAdd className="text-sm" />
                    {t('common.add') || 'Dodaj'}
                  </button>
                </div>
                
                {manualFriend.flexibleRates.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                    {t('friends.noRates') || 'Nema definisanih cena. Kliknite "Dodaj" da dodate.'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {manualFriend.flexibleRates.map((rate, index) => (
                      <div key={index} className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            {t('friends.ratePeriod') || 'Period'} {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setManualFriend({
                                ...manualFriend,
                                flexibleRates: manualFriend.flexibleRates.filter((_, i) => i !== index)
                              });
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <MdClose className="text-sm" />
                          </button>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              {t('friends.days') || 'Dani'}
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'].map((day, dayIndex) => {
                                // Map to actual day numbers: Pon=1, Uto=2, ..., Sub=6, Ned=0
                                const actualDayIndex = dayIndex < 6 ? dayIndex + 1 : 0;
                                return (
                                  <label key={dayIndex} className="flex items-center gap-1 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={rate.days.includes(actualDayIndex)}
                                      onChange={(e) => {
                                        const newRates = [...manualFriend.flexibleRates];
                                        if (e.target.checked) {
                                          newRates[index].days = [...newRates[index].days, actualDayIndex].sort();
                                        } else {
                                          newRates[index].days = newRates[index].days.filter(d => d !== actualDayIndex);
                                        }
                                        setManualFriend({ ...manualFriend, flexibleRates: newRates });
                                      }}
                                      className="w-3 h-3"
                                    />
                                    <span className="text-xs">{day}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                {t('calendar.startTime') || 'Od'}
                              </label>
                              <input
                                type="time"
                                value={rate.startTime}
                                onChange={(e) => {
                                  const newRates = [...manualFriend.flexibleRates];
                                  newRates[index].startTime = e.target.value;
                                  setManualFriend({ ...manualFriend, flexibleRates: newRates });
                                }}
                                className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                {t('calendar.endTime') || 'Do'}
                              </label>
                              <input
                                type="time"
                                value={rate.endTime}
                                onChange={(e) => {
                                  const newRates = [...manualFriend.flexibleRates];
                                  newRates[index].endTime = e.target.value;
                                  setManualFriend({ ...manualFriend, flexibleRates: newRates });
                                }}
                                className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              {t('friends.rate') || 'Cena (RSD/h)'}
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={rate.rate}
                              onChange={(e) => {
                                const newRates = [...manualFriend.flexibleRates];
                                newRates[index].rate = parseFloat(e.target.value) || 0;
                                setManualFriend({ ...manualFriend, flexibleRates: newRates });
                              }}
                              className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={addingFriend}
                  className="btn-primary flex-1"
                >
                  {addingFriend ? t('friends.adding') || 'Dodavanje...' : t('friends.add') || 'Dodaj'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddFriend(false);
                    setManualFriend({
                      name: '',
                      email: '',
                      phone: '',
                      relationshipType: 'babysitter',
                      flexibleRates: []
                    });
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Friends List */}
      {friends.length === 0 ? (
        <div className="card text-center py-12">
          <MdPeople className="text-5xl text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 mb-2">
            {t('friends.noFriends') || 'No friends connected'}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {t('friends.connectMessage') || 'Connect with friends to share calendar and track together'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {friends.map((friend) => (
            <div
              key={friendRowKey(friend)}
              className={`card p-4 hover:shadow-md transition-shadow ${
                friend.isShared
                  ? 'border-2 border-dashed border-primary-200/90 dark:border-primary-700/45 bg-gradient-to-br from-slate-50/95 via-white to-primary-50/40 dark:from-gray-900/80 dark:via-gray-900/60 dark:to-primary-950/25 opacity-[0.93]'
                  : ''
              }`}
            >
              <div className={`flex gap-4 ${isMobile ? 'flex-col' : 'items-start justify-between'}`}>
                {/* Mobile: row 1 = avatar + name + three-dots on same line */}
                {isMobile && (
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden">
                      {friend.photoUrl || friend.photo_url || friend.profileImage ? (
                        <img 
                          src={friend.photoUrl || friend.photo_url || friend.profileImage} 
                          alt={friend.name || friend.email || 'Friend'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full flex items-center justify-center ${friend.photoUrl || friend.photo_url || friend.profileImage ? 'hidden' : ''}`}>
                        {friend.name?.[0]?.toUpperCase() || friend.email?.[0]?.toUpperCase() || 'F'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {friend.name || friend.email}
                      </h3>
                      {friend.isShared && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary-100/90 dark:bg-primary-900/50 text-primary-800 dark:text-primary-200 whitespace-nowrap">
                          {t('friends.sharedBabysitterBadge') || 'Preko deljenja'}
                        </span>
                      )}
                      {!friend.hasAccount && (
                        <span className="inline-flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400 whitespace-nowrap">
                          (<button
                            type="button"
                            onClick={() => setInviteFriendDialog({ isOpen: true, friend })}
                            className="text-yellow-600 dark:text-yellow-400 hover:underline font-medium"
                            title={t('friends.inviteToJoin') || 'Pozovi prijatelja da se pridruži'}
                          >
                            {t('friends.inviteFriend') || 'Pozovi prijatelja'}
                          </button>
                          )
                        </span>
                      )}
                    </div>
                    <div className="relative flex-shrink-0" ref={(el) => { if (el) menuRefs.current[friendRowKey(friend)] = el; }}>
                      <button 
                        onClick={() => setOpenMenuId(openMenuId === friendRowKey(friend) ? null : friendRowKey(friend))}
                        className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                        title={t('friends.moreOptions') || 'Više opcija'}
                      >
                        <MdMoreVert className="text-xl" />
                      </button>
                    </div>
                  </div>
                )}
                <div className={`flex items-center gap-4 flex-1 min-w-0 ${isMobile ? 'hidden' : ''}`}>
                  <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden">
                    {friend.photoUrl || friend.photo_url || friend.profileImage ? (
                      <img 
                        src={friend.photoUrl || friend.photo_url || friend.profileImage} 
                        alt={friend.name || friend.email || 'Friend'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center ${friend.photoUrl || friend.photo_url || friend.profileImage ? 'hidden' : ''}`}>
                      {friend.name?.[0]?.toUpperCase() || friend.email?.[0]?.toUpperCase() || 'F'}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        {friend.name || friend.email}
                      </h3>
                      {friend.isShared && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary-100/90 dark:bg-primary-900/50 text-primary-800 dark:text-primary-200 whitespace-nowrap">
                          {t('friends.sharedBabysitterBadge') || 'Preko deljenja'}
                        </span>
                      )}
                      {!friend.hasAccount && !friend.isShared && (
                        <span className="inline-flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400 whitespace-nowrap">
                          ({t('friends.notMember') || 'Nije član'},{' '}
                          <button
                            type="button"
                            onClick={() => setInviteFriendDialog({ isOpen: true, friend })}
                            className="text-yellow-600 dark:text-yellow-400 hover:underline font-medium"
                            title={t('friends.inviteToJoin') || 'Pozovi prijatelja da se pridruži'}
                          >
                            {t('friends.inviteFriend') || 'Pozovi prijatelja'}
                          </button>
                          )
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {friend.email}
                    </p>
                    {friend.isShared && friend.sharedByName && (
                      <p className="text-xs text-primary-600/90 dark:text-primary-400/90 mt-0.5">
                        {(t('calendar.sharedBy') || 'Podeljeno od')}: {friend.sharedByName}
                      </p>
                    )}
                    {!isMobile && friend.hourlyRate > 0 && (
                      <p className="text-sm text-primary-600 font-semibold mt-1">
                        {t('friends.hourlyRate') || 'Hourly Rate'}: {friend.hourlyRate} RSD/h
                      </p>
                    )}
                    {/* All info - desktop: inline; mobile: moved to full-width block below */}
                    {!isMobile && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      {getPaymentScheduleLabel(friend) && (
                        <span className="flex items-center gap-1">
                          <MdSchedule className="text-primary-600 text-sm" />
                          <span className="text-primary-600 dark:text-primary-400">{getPaymentScheduleLabel(friend)}</span>
                        </span>
                      )}
                      {getFullTimeWorkLabel(friend) && (
                        <span className="flex items-center gap-1">
                          {getPaymentScheduleLabel(friend) && <span className="mx-1">·</span>}
                          <MdWork className="text-green-600 text-sm" />
                          <span className="text-green-600 dark:text-green-400">{getFullTimeWorkLabel(friend)}</span>
                        </span>
                      )}
                      {(getPaymentScheduleLabel(friend) || getFullTimeWorkLabel(friend)) && <span className="mx-1">·</span>}
                      {friend.relationshipType === 'babysitter' && (
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          friend.babysitterStatus === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : friend.babysitterStatus === 'inactive' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {friend.babysitterStatus === 'active' ? (t('friends.statusActive') || 'Radi') : friend.babysitterStatus === 'inactive' ? (t('friends.statusInactive') || 'Ne radi') : (t('friends.statusNotSet') || 'Status nije postavljen')}
                        </span>
                      )}
                      {(getPaymentScheduleLabel(friend) || getFullTimeWorkLabel(friend) || friend.relationshipType === 'babysitter') && <span className="mx-1">·</span>}
                      <span>{t('friends.connected') || 'Povezan'}: {new Date(friend.connectedDate).toLocaleDateString('sr-RS', { day: 'numeric', month: 'numeric', year: 'numeric' })}</span>
                    </div>
                    )}
                  </div>
                </div>
                {/* Mobile: email then tag on new lines */}
                {isMobile && (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate -mt-2">
                      {friend.email}
                    </p>
                    {friend.isShared && friend.sharedByName && (
                      <p className="text-xs text-primary-600/90 dark:text-primary-400/90">
                        {(t('calendar.sharedBy') || 'Podeljeno od')}: {friend.sharedByName}
                      </p>
                    )}
                    {friend.relationshipType && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full self-start ${
                          friend.relationshipType === 'babysitter' 
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                            : 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        }`}
                      >
                        {friend.relationshipType === 'babysitter' 
                          ? (t('friends.babysitter') || 'Bebisiterka')
                          : (t('friends.family') || 'Porodica')}
                      </span>
                    )}
                  </div>
                )}
                {/* Right column: tag + actions (desktop only); on mobile tag/actions are above/below */}
                {!isMobile && (
                <div className="flex flex-col gap-2 flex-shrink-0 items-end">
                  {friend.relationshipType && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        friend.relationshipType === 'babysitter' 
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      }`}
                    >
                      {friend.relationshipType === 'babysitter' 
                        ? (t('friends.babysitter') || 'Bebisiterka')
                        : (t('friends.family') || 'Porodica')}
                    </span>
                  )}
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          if (!friend.hasAccount) {
                            showToast(t('friends.noAccountMessage') || 'Prijatelj nema nalog još uvek. Ne možete slati poruke dok prijatelj ne kreira nalog.', 'info');
                            return;
                          }
                          handleSendMessage(friend.friendUserId || friend.id);
                        }}
                        className={`p-2 rounded-xl ${
                          friend.hasAccount 
                            ? 'hover:bg-primary-100 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                            : 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500'
                        }`}
                        title={friend.hasAccount ? (t('friends.sendMessage') || 'Pošalji poruku') : (t('friends.noAccountMessage') || 'Prijatelj nema nalog još uvek')}
                        disabled={!friend.hasAccount}
                      >
                        <MdMessage className="text-xl" />
                      </button>
                      <div className="flex items-center gap-1">
                        {!friend.isShared && hasAnyShared(friend) && (
                          <span className="text-[10px] sm:text-xs text-primary-600 dark:text-primary-400 max-w-[80px] sm:max-w-[120px] truncate" title={getSharedLabel(friend)}>
                            {t('friends.shared') || 'Podeljeno'}: {getSharedLabel(friend)}
                          </span>
                        )}
                        {!friend.isShared && (
                          <button
                            onClick={() => handleShareCalendar(friend.id)}
                            disabled={!friend.hasAccount}
                            className={`p-2 rounded-xl ${
                              hasAnyShared(friend) ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 ' : ''
                            }${friend.hasAccount 
                              ? 'hover:bg-gray-100 dark:hover:bg-gray-700' 
                              : 'opacity-50 cursor-not-allowed'
                            }`}
                            title={friend.hasAccount ? (hasAnyShared(friend) ? getSharedLabel(friend) : (t('friends.shareCalendar') || 'Podeli moj kalendar')) : (t('friends.noAccountShare') || 'Ne možete podeliti kalendar sa prijateljem koji nema nalog')}
                          >
                            <MdShare className="text-xl" />
                          </button>
                        )}
                      </div>
                      <div className="relative" ref={(el) => { if (el) menuRefs.current[friendRowKey(friend)] = el; }}>
                        <button 
                          onClick={() => setOpenMenuId(openMenuId === friendRowKey(friend) ? null : friendRowKey(friend))}
                          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                          title={t('friends.moreOptions') || 'Više opcija'}
                        >
                          <MdMoreVert className="text-xl" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                )}
                <DropdownOrBottomSheet
                          open={openMenuId === friendRowKey(friend)}
                          onClose={() => setOpenMenuId(null)}
                          triggerRef={menuRefs.current[friendRowKey(friend)] ? { current: menuRefs.current[friendRowKey(friend)] } : null}
                          title={friend.name || friend.email || (t('friends.moreOptions') || 'Više opcija')}
                        >
                          <div className="min-w-[200px]">
                            {isMobile && (
                              <>
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    if (!friend.hasAccount) {
                                      showToast(t('friends.noAccountMessage') || 'Prijatelj nema nalog još uvek.', 'info');
                                      return;
                                    }
                                    handleSendMessage(friend.friendUserId || friend.id);
                                  }}
                                  disabled={!friend.hasAccount}
                                  className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                                    friend.hasAccount ? 'text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700' : 'text-gray-400 dark:text-gray-500'
                                  }`}
                                >
                                  <MdMessage className="text-lg" />
                                  {t('friends.sendMessage') || 'Pošalji poruku'}
                                </button>
                                {!friend.isShared && (
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleShareCalendar(friend.id);
                                    }}
                                    disabled={!friend.hasAccount}
                                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                                      friend.hasAccount ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700' : 'text-gray-400 dark:text-gray-500'
                                    }`}
                                  >
                                    <MdShare className="text-lg" />
                                    {t('friends.shareCalendar') || 'Podeli kalendar'}
                                  </button>
                                )}
                                <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                              </>
                            )}
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                handleEditFriend(friend);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <MdEdit className="text-lg" />
                              {t('friends.editFriend') || 'Izmeni prijatelja'}
                            </button>
                            {friend.relationshipType === 'babysitter' && (
                              <>
                                {friend.paymentSchedule ? (
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setEditingPaymentSchedule(friend);
                                      // Convert old format to new format
                                      const oldSchedule = friend.paymentSchedule || {};
                                      let scheduleType = 'monthly';
                                      let days = [];
                                      let time = '09:00';
                                      let repeatType = 'monthly';
                                      let intervalWeeks = 2;
                                      let weekday = 'friday';
                                      let anchorDate = null;
                                      
                                      if (oldSchedule.scheduleType) {
                                        scheduleType = oldSchedule.scheduleType;
                                        days = (oldSchedule.days || []).map(d => {
                                          if (d === '15' || d === 15) return 'fifteenth';
                                          return d;
                                        });
                                        repeatType = oldSchedule.repeatType || 'monthly';
                                        if (scheduleType === 'weekly' && oldSchedule.intervalWeeks && parseInt(oldSchedule.intervalWeeks, 10) > 1) {
                                          scheduleType = 'weeklyInterval';
                                        }
                                      } else if (oldSchedule.days && Array.isArray(oldSchedule.days)) {
                                        const isWeekly = oldSchedule.days.some(d => ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(d));
                                        scheduleType = isWeekly ? 'weekly' : 'monthly';
                                        days = oldSchedule.days.map(d => {
                                          if (d === '15' || d === 15) return 'fifteenth';
                                          return d;
                                        });
                                        repeatType = oldSchedule.repeatType || 'monthly';
                                        if (isWeekly && oldSchedule.intervalWeeks && parseInt(oldSchedule.intervalWeeks, 10) > 1) {
                                          scheduleType = 'weeklyInterval';
                                        }
                                      } else if (oldSchedule.dayType) {
                                        scheduleType = 'monthly';
                                        if (oldSchedule.dayType === 'first') {
                                          days = ['first'];
                                        } else if (oldSchedule.dayType === 'last') {
                                          days = ['last'];
                                        } else if (oldSchedule.dayType === 'specific' && oldSchedule.specificDay) {
                                          const day = oldSchedule.specificDay;
                                          if (day === 15) {
                                            days = ['fifteenth'];
                                          } else {
                                            days = [String(day)];
                                          }
                                        }
                                        repeatType = oldSchedule.repeatType || 'monthly';
                                      } else if (oldSchedule.dayOfMonth) {
                                        scheduleType = 'monthly';
                                        const day = oldSchedule.dayOfMonth;
                                        if (day === 1) {
                                          days = ['first'];
                                        } else if (day === 15) {
                                          days = ['fifteenth'];
                                        } else {
                                          days = [String(day)];
                                        }
                                        repeatType = oldSchedule.repeatType || 'monthly';
                                      } else if (oldSchedule.date) {
                                        const scheduleDate = new Date(oldSchedule.date);
                                        repeatType = oldSchedule.repeatType || 'monthly';
                                        anchorDate = typeof oldSchedule.date === 'string'
                                          ? oldSchedule.date.split('T')[0]
                                          : formatLocalYMD(scheduleDate);

                                        if (repeatType === 'weekly') {
                                          // New weekly format uses date as anchor
                                          const map = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                                          weekday = map[scheduleDate.getDay()] || 'friday';
                                          intervalWeeks = parseInt(oldSchedule.intervalWeeks, 10) > 1 ? parseInt(oldSchedule.intervalWeeks, 10) : 2;
                                          scheduleType = (parseInt(oldSchedule.intervalWeeks, 10) > 1) ? 'weeklyInterval' : 'weekly';
                                          days = [weekday];
                                        } else {
                                          const scheduleDay = scheduleDate.getDate();
                                          if (scheduleDay === 1) {
                                            days = ['first'];
                                          } else if (scheduleDay === 15) {
                                            days = ['fifteenth'];
                                          } else {
                                            days = [String(scheduleDay)];
                                          }
                                        }
                                      }
                                      
                                      if (oldSchedule.time) {
                                        time = oldSchedule.time;
                                      }
                                      if (oldSchedule.intervalWeeks && parseInt(oldSchedule.intervalWeeks, 10) > 1) {
                                        intervalWeeks = parseInt(oldSchedule.intervalWeeks, 10);
                                      }
                                      if (Array.isArray(oldSchedule.days) && oldSchedule.days.length === 1 && ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].includes(oldSchedule.days[0])) {
                                        weekday = oldSchedule.days[0];
                                      }
                                      
                                      setPaymentSchedule({ 
                                        scheduleType: scheduleType,
                                        days: days,
                                        time: time,
                                        repeatType: repeatType,
                                        intervalWeeks,
                                        weekday,
                                        anchorDate,
                                      });
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                  >
                                    <MdSchedule className="text-lg" />
                                    {t('friends.paymentScheduleMenu') || 'Dinamika placanja'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setEditingPaymentSchedule(friend);
                                      setPaymentSchedule({ 
                                        scheduleType: 'monthly',
                                        days: [],
                                        time: '09:00',
                                        intervalWeeks: 2,
                                        weekday: 'friday',
                                        anchorDate: null,
                                      });
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                  >
                                    <MdSchedule className="text-lg" />
                                    {t('friends.paymentScheduleMenu') || 'Dinamika placanja'}
                                  </button>
                                )}
                                {friend.fullTimeWork && friend.fullTimeWork.enabled ? (
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setEditingFullTimeWork(friend);
                                      setFullTimeWork({
                                        enabled: friend.fullTimeWork?.enabled || false,
                                        startTime: friend.fullTimeWork?.startTime || '09:00',
                                        endTime: friend.fullTimeWork?.endTime || '17:00',
                                        days: friend.fullTimeWork?.days || []
                                      });
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                  >
                                    <MdWork className="text-lg" />
                                    {t('friends.workingHoursMenu') || 'Radno vreme'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setEditingFullTimeWork(friend);
                                      setFullTimeWork({
                                        enabled: false,
                                        startTime: '09:00',
                                        endTime: '17:00',
                                        days: []
                                      });
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                  >
                                    <MdWork className="text-lg" />
                                    {t('friends.workingHoursMenu') || 'Radno vreme'}
                                  </button>
                                )}
                                {friend.relationshipType === 'babysitter' && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const targetFriendshipId = friend.friendshipId || friend.id;
                                        const newStatus = friend.babysitterStatus === 'active' ? 'inactive' : 'active';
                                        await api.put(`/friends/${targetFriendshipId}/babysitter-status`, { status: newStatus });
                                        setFriends(friends.map(f => 
                                          f.friendshipId === targetFriendshipId 
                                            ? { ...f, babysitterStatus: newStatus }
                                            : f
                                        ));
                                        showToast(
                                          newStatus === 'active' 
                                            ? (t('friends.statusSetActive') || 'Status je postavljen na "Radi"')
                                            : (t('friends.statusSetInactive') || 'Status je postavljen na "Ne radi"'),
                                          'success'
                                        );
                                        setOpenMenuId(null);
                                      } catch (error) {
                                        console.error('Error updating babysitter status:', error);
                                        showToast(t('friends.statusUpdateError') || 'Greška pri ažuriranju statusa: ' + (error.response?.data?.message || error.message), 'error');
                                      }
                                    }}
                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                                      friend.babysitterStatus === 'active'
                                        ? 'text-green-600 dark:text-green-400'
                                        : friend.babysitterStatus === 'inactive'
                                        ? 'text-red-600 dark:text-red-400'
                                        : 'text-gray-600 dark:text-gray-400'
                                    }`}
                                  >
                                    <MdCheckCircle className="text-lg" />
                                    {friend.babysitterStatus === 'active'
                                      ? (t('friends.setStatusInactive') || 'Postavi na "Ne radi"')
                                      : (t('friends.setStatusActive') || 'Postavi na "Radi"')}
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    setEditingFriend(friend);
                                    setEditFriendForm({
                                      name: friend.name || '',
                                      email: friend.email || '',
                                      phone: friend.phone || '',
                                      hourlyRate: friend.hourlyRate || '',
                                      flexibleRates: Array.isArray(friend.flexibleRates) ? friend.flexibleRates : []
                                    });
                                    setEditingFriendRates(true);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <MdAttachMoney className="text-lg" />
                                  {t('friends.editRatesMenu') || 'Satnica i cene'}
                                </button>
                                {friend.relationshipType === 'babysitter' && (
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setEditingReminders(friend);
                                      setReminderSettings({
                                        reminder7d: true,
                                        reminder3d: true,
                                        reminder0d: true,
                                        reminderPlus3d: true,
                                        emailEnabled: true,
                                        notificationEnabled: true,
                                      });
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                  >
                                    <MdNotifications className="text-lg" />
                                    {t('friends.setRemindersMenu') || 'Postavi podsetnike'}
                                  </button>
                                )}
                              </>
                            )}
                            {!friend.isShared && (
                              <button
                                onClick={() => {
                                  setDeleteConfirm({ isOpen: true, friendId: friend.id });
                                  setOpenMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                              >
                                <MdDelete className="text-lg" />
                                {t('friends.delete') || 'Obriši'}
                              </button>
                            )}
                          </div>
                        </DropdownOrBottomSheet>
                {isMobile && (friend.hourlyRate > 0 || getPaymentScheduleLabel(friend) || getFullTimeWorkLabel(friend) || friend.relationshipType === 'babysitter' || friend.connectedDate) && (
                  <div className="w-full pt-3 mt-1 border-t border-gray-200 dark:border-gray-600 space-y-2 text-xs text-gray-600 dark:text-gray-400">
                    {friend.hourlyRate > 0 && (
                      <p className="text-primary-600 dark:text-primary-400 font-semibold">
                        {t('friends.hourlyRate') || 'Satnica'}: {friend.hourlyRate} RSD/h
                      </p>
                    )}
                    {getPaymentScheduleLabel(friend) && (
                      <p className="text-primary-600 dark:text-primary-400">• {getPaymentScheduleLabel(friend)}</p>
                    )}
                    {getFullTimeWorkLabel(friend) && (
                      <p className="text-green-600 dark:text-green-400">{getFullTimeWorkLabel(friend)}</p>
                    )}
                    {friend.relationshipType === 'babysitter' && (
                      <p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          friend.babysitterStatus === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                          friend.babysitterStatus === 'inactive' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {friend.babysitterStatus === 'active' ? (t('friends.statusActive') || 'Radi') : friend.babysitterStatus === 'inactive' ? (t('friends.statusInactive') || 'Ne radi') : (t('friends.statusNotSet') || 'Status nije postavljen')}
                        </span>
                      </p>
                    )}
                    {friend.connectedDate && (
                      <p>{t('friends.connected') || 'Povezan'}: {new Date(friend.connectedDate).toLocaleDateString('sr-RS', { day: 'numeric', month: 'numeric', year: 'numeric' })}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
        </div>
      </div>

      {/* Payment Schedule Modal */}
      {editingPaymentSchedule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('friends.paymentSchedule') || 'Dinamika plaćanja'}
              </h3>
              <button
                onClick={() => {
                  setEditingPaymentSchedule(null);
                  setPaymentSchedule({ 
                    scheduleType: 'monthly',
                    days: [],
                    time: '09:00',
                    intervalWeeks: 2,
                    weekday: 'friday',
                    anchorDate: null,
                  });
                }}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Schedule Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('friends.paymentScheduleType') || 'Tip dinamike plaćanja'}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduleType"
                      value="monthly"
                      checked={paymentSchedule.scheduleType === 'monthly'}
                      onChange={() => {
                        setPaymentSchedule({ 
                          ...paymentSchedule, 
                          scheduleType: 'monthly',
                          days: [],
                          anchorDate: null,
                        });
                      }}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm">{t('friends.monthly') || 'Mesecno'}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduleType"
                      value="weekly"
                      checked={paymentSchedule.scheduleType === 'weekly'}
                      onChange={() => {
                        setPaymentSchedule({ 
                          ...paymentSchedule, 
                          scheduleType: 'weekly',
                          days: [],
                          anchorDate: null,
                        });
                      }}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm">{t('friends.weekly') || 'Nedeljno'}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduleType"
                      value="weeklyInterval"
                      checked={paymentSchedule.scheduleType === 'weeklyInterval'}
                      onChange={() => {
                        setPaymentSchedule({
                          ...paymentSchedule,
                          scheduleType: 'weeklyInterval',
                          // for interval schedule, keep a single weekday
                          weekday: paymentSchedule.weekday || 'friday',
                          intervalWeeks: paymentSchedule.intervalWeeks || 2,
                          days: [],
                          anchorDate: paymentSchedule.anchorDate || null,
                        });
                      }}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm">{t('friends.everyNth') || 'Svaki n-ti'}</span>
                  </label>
                </div>
              </div>

              {/* Monthly Schedule Options */}
              {paymentSchedule.scheduleType === 'monthly' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('friends.paymentDaysInMonth') || 'Dani plaćanja u mesecu'}
                    </label>
                    
                    {/* Quick shortcuts - Redosled: Prvi dan, Polovina meseca, Poslednji dan */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => {
                          const newDays = [...(paymentSchedule.days || [])];
                          if (!newDays.includes('first')) {
                            newDays.push('first');
                            setPaymentSchedule({ ...paymentSchedule, days: newDays });
                          } else {
                            // Remove if already selected
                            const filtered = newDays.filter(d => d !== 'first');
                            setPaymentSchedule({ ...paymentSchedule, days: filtered });
                          }
                        }}
                        className={`px-3 py-1.5 text-sm rounded-xl border transition-colors ${
                          (paymentSchedule.days || []).includes('first')
                            ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-500 text-primary-700 dark:text-primary-300'
                            : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        {t('friends.firstDay') || 'Prvi dan'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newDays = [...(paymentSchedule.days || [])];
                          // Handle both '15' and 'fifteenth' for compatibility
                          const hasFifteenth = newDays.includes('fifteenth') || newDays.includes('15') || newDays.includes(15);
                          if (!hasFifteenth) {
                            // Remove '15' if exists and add 'fifteenth'
                            const filtered = newDays.filter(d => d !== '15' && d !== 15);
                            filtered.push('fifteenth');
                            setPaymentSchedule({ ...paymentSchedule, days: filtered });
                          } else {
                            // Remove if already selected
                            const filtered = newDays.filter(d => d !== 'fifteenth' && d !== '15' && d !== 15);
                            setPaymentSchedule({ ...paymentSchedule, days: filtered });
                          }
                        }}
                        className={`px-3 py-1.5 text-sm rounded-xl border transition-colors ${
                          (paymentSchedule.days || []).includes('fifteenth') || (paymentSchedule.days || []).includes('15') || (paymentSchedule.days || []).includes(15)
                            ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-500 text-primary-700 dark:text-primary-300'
                            : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        {t('friends.fifteenthDay') || 'Polovina meseca'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newDays = [...(paymentSchedule.days || [])];
                          if (!newDays.includes('last')) {
                            newDays.push('last');
                            setPaymentSchedule({ ...paymentSchedule, days: newDays });
                          } else {
                            // Remove if already selected
                            const filtered = newDays.filter(d => d !== 'last');
                            setPaymentSchedule({ ...paymentSchedule, days: filtered });
                          }
                        }}
                        className={`px-3 py-1.5 text-sm rounded-xl border transition-colors ${
                          (paymentSchedule.days || []).includes('last')
                            ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-500 text-primary-700 dark:text-primary-300'
                            : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        {t('friends.lastDay') || 'Poslednji dan'}
                      </button>
                    </div>

                    {/* Custom day input */}
                    <div className="flex gap-2 mb-3 flex-wrap sm:flex-nowrap">
                      <input
                        type="number"
                        min="1"
                        max="31"
                        placeholder={t('friends.specificDay') || 'Određeni dan (1-31)'}
                        className="flex-1 min-w-[220px] px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const dayValue = e.target.value;
                            if (dayValue && parseInt(dayValue) >= 1 && parseInt(dayValue) <= 31) {
                              const newDays = [...(paymentSchedule.days || [])];
                              if (!newDays.includes(dayValue)) {
                                newDays.push(dayValue);
                                setPaymentSchedule({ ...paymentSchedule, days: newDays });
                                e.target.value = '';
                              }
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          const input = e.target.previousElementSibling;
                          const dayValue = input.value;
                          if (dayValue && parseInt(dayValue) >= 1 && parseInt(dayValue) <= 31) {
                            const newDays = [...(paymentSchedule.days || [])];
                            if (!newDays.includes(dayValue)) {
                              newDays.push(dayValue);
                              setPaymentSchedule({ ...paymentSchedule, days: newDays });
                              input.value = '';
                            }
                          }
                        }}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-medium transition-colors"
                      >
                        {t('common.add') || 'Dodaj'}
                      </button>
                    </div>
                  </div>

                </>
              )}

              {/* Weekly Schedule Options */}
              {paymentSchedule.scheduleType === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('friends.paymentDaysInWeek') || 'Dani plaćanja u nedelji'}
                  </label>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {[
                      { value: 'monday', label: t('calendar.monday') || 'Ponedeljak' },
                      { value: 'tuesday', label: t('calendar.tuesday') || 'Utorak' },
                      { value: 'wednesday', label: t('calendar.wednesday') || 'Sreda' },
                      { value: 'thursday', label: t('calendar.thursday') || 'Četvrtak' },
                      { value: 'friday', label: t('calendar.friday') || 'Petak' },
                      { value: 'saturday', label: t('calendar.saturday') || 'Subota' },
                      { value: 'sunday', label: t('calendar.sunday') || 'Nedelja' }
                    ].map((day) => (
                      <label key={day.value} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                        <input
                          type="checkbox"
                          checked={(paymentSchedule.days || []).includes(day.value)}
                          onChange={(e) => {
                            const newDays = [...(paymentSchedule.days || [])];
                            if (e.target.checked) {
                              if (!newDays.includes(day.value)) {
                                newDays.push(day.value);
                              }
                            } else {
                              const index = newDays.indexOf(day.value);
                              if (index > -1) {
                                newDays.splice(index, 1);
                              }
                            }
                            setPaymentSchedule({ ...paymentSchedule, days: newDays });
                          }}
                          className="w-4 h-4 text-primary-600 rounded"
                        />
                        <span className="text-sm">{day.label}</span>
                      </label>
                    ))}
                  </div>
                  
                  {/* Selected days list */}
                  {(paymentSchedule.days || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {(paymentSchedule.days || []).map((day, index) => {
                        const dayLabels = {
                          monday: t('calendar.monday') || 'Ponedeljak',
                          tuesday: t('calendar.tuesday') || 'Utorak',
                          wednesday: t('calendar.wednesday') || 'Sreda',
                          thursday: t('calendar.thursday') || 'Četvrtak',
                          friday: t('calendar.friday') || 'Petak',
                          saturday: t('calendar.saturday') || 'Subota',
                          sunday: t('calendar.sunday') || 'Nedelja'
                        };
                        return (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg text-sm"
                          >
                            {dayLabels[day] || day}
                            <button
                              type="button"
                              onClick={() => {
                                const newDays = (paymentSchedule.days || []).filter((_, i) => i !== index);
                                setPaymentSchedule({ ...paymentSchedule, days: newDays });
                              }}
                              className="ml-1 hover:text-red-600 dark:hover:text-red-400"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Weekly Interval Schedule Options (every N weeks on chosen weekday) */}
              {paymentSchedule.scheduleType === 'weeklyInterval' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('friends.everyNthWeeks') || 'Svake n-te nedelje'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="52"
                      value={paymentSchedule.intervalWeeks ?? 2}
                      onChange={(e) => setPaymentSchedule({ ...paymentSchedule, intervalWeeks: parseInt(e.target.value || '1', 10) })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('friends.everyNthWeeksHint') || 'Primer: 2 = svaki drugi, 3 = svaki treći.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('friends.weekday') || 'Dan u nedelji'}
                    </label>
                    <select
                      value={paymentSchedule.weekday || 'friday'}
                      onChange={(e) => setPaymentSchedule({ ...paymentSchedule, weekday: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      {[
                        { value: 'monday', label: weekdayToLabel('monday') },
                        { value: 'tuesday', label: weekdayToLabel('tuesday') },
                        { value: 'wednesday', label: weekdayToLabel('wednesday') },
                        { value: 'thursday', label: weekdayToLabel('thursday') },
                        { value: 'friday', label: weekdayToLabel('friday') },
                        { value: 'saturday', label: weekdayToLabel('saturday') },
                        { value: 'sunday', label: weekdayToLabel('sunday') },
                      ].map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Time Picker - Always visible */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('friends.paymentTime') || 'Vreme'}
                </label>
                <input
                  type="time"
                  value={paymentSchedule.time || '09:00'}
                  onChange={(e) => setPaymentSchedule({ ...paymentSchedule, time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              {/* Preview - show after time selection */}
              {paymentSchedule.scheduleType === 'weeklyInterval' && (
                <div className="p-3 rounded-lg border border-primary-200 bg-primary-50/60 text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-200">
                  <p className="text-xs font-semibold mb-1">
                    {t('friends.schedulePreview') || 'Rezultat'}:
                  </p>
                  <p className="text-sm font-semibold">
                    {(() => {
                      const interval = parseInt(paymentSchedule.intervalWeeks, 10);
                      const weekday = paymentSchedule.weekday;
                      const time = paymentSchedule.time || '09:00';
                      if (!Number.isFinite(interval) || interval < 1 || !weekday) return '';
                      return `${t('friends.every') || 'Svaki'} ${interval}. ${weekdayToFullLabel(weekday)} ${t('friends.atTime') || 'u'} ${time}`;
                    })()}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    try {
                      let scheduleToSend;

                      if (paymentSchedule.scheduleType === 'weeklyInterval') {
                        const interval = parseInt(paymentSchedule.intervalWeeks, 10);
                        const weekday = paymentSchedule.weekday;
                        if (!Number.isFinite(interval) || interval < 1) {
                          showToast(t('friends.invalidInterval') || 'Unesite validan broj (n ≥ 1).', 'error');
                          return;
                        }
                        if (!weekday) {
                          showToast(t('friends.noWeekdaySelected') || 'Izaberite dan u nedelji.', 'error');
                          return;
                        }

                        // Keep existing anchor when editing; otherwise anchor to next occurrence from today
                        const rawAnchorDate = paymentSchedule.anchorDate || nextDateForWeekday(weekday);
                        const anchorDate = normalizeAnchorToWeekday(rawAnchorDate, weekday);
                        if (!anchorDate) {
                          showToast(t('friends.scheduleUpdateError') || 'Greška pri određivanju datuma početka.', 'error');
                          return;
                        }

                        scheduleToSend = {
                          scheduleType: 'weekly',
                          days: [weekday],
                          time: paymentSchedule.time || '09:00',
                          repeatType: 'weekly',
                          intervalWeeks: interval,
                          date: anchorDate,
                        };
                      } else {
                        // Validate days array
                        if (!paymentSchedule.days || !Array.isArray(paymentSchedule.days) || paymentSchedule.days.length === 0) {
                          showToast(t('friends.noDaysSelected') || 'Izaberite barem jedan dan za plaćanje.', 'error');
                          return;
                        }

                        // Ensure days array contains valid values
                        const validDays = paymentSchedule.days.filter(day => day && day.toString().trim() !== '');
                        if (validDays.length === 0) {
                          showToast(t('friends.noDaysSelected') || 'Izaberite barem jedan dan za plaćanje.', 'error');
                          return;
                        }

                        // Convert '15' to 'fifteenth' before sending
                        const normalizedDays = validDays.map(d => {
                          if (d === '15' || d === 15) return 'fifteenth';
                          return d;
                        });

                        scheduleToSend = {
                          scheduleType: paymentSchedule.scheduleType || 'monthly',
                          days: normalizedDays,
                          time: paymentSchedule.time || '09:00',
                          repeatType: paymentSchedule.repeatType || (paymentSchedule.scheduleType === 'weekly' ? 'weekly' : 'monthly')
                        };
                      }
                      
                      // Use friendshipId (the ID from friendships table) for the API call
                      const targetFriendshipId = editingPaymentSchedule?.friendshipId 
                        || editingPaymentSchedule?.id; // Fallback to id if friendshipId not available
                      
                      if (!targetFriendshipId) {
                        console.error('Missing friendshipId in editingPaymentSchedule:', editingPaymentSchedule);
                        showToast(t('friends.scheduleUpdateError') || 'Nedostaje identifikator prijatelja za ažuriranje dinamike plaćanja.', 'error');
                        return;
                      }

                      if (import.meta.env.DEV) console.log('Sending payment schedule update:', {
                        targetFriendshipId,
                        scheduleToSend,
                        editingPaymentSchedule
                      });

                      await api.put(`/friends/${targetFriendshipId}/payment-schedule`, scheduleToSend);
                      const updatedFriends = friends.map(f => 
                        f.friendshipId === targetFriendshipId 
                          ? { ...f, paymentSchedule: scheduleToSend }
                          : f
                      );
                      setFriends(updatedFriends);
                      setEditingPaymentSchedule(null);
                      showToast(t('friends.scheduleUpdated') || 'Dinamika plaćanja je ažurirana.', 'success');
                    } catch (error) {
                      console.error('Error updating payment schedule:', error);
                      console.error('Error details:', error.response?.data || error.message);
                      showToast(t('friends.scheduleUpdateError') || 'Greška pri ažuriranju dinamike plaćanja: ' + (error.response?.data?.message || error.message), 'error');
                    }
                  }}
                  className="btn-primary flex-1"
                >
                  {t('common.save') || 'Sačuvaj'}
                </button>
                <button
                  onClick={() => {
                    setEditingPaymentSchedule(null);
                    setPaymentSchedule({ 
                      scheduleType: 'monthly',
                      days: [],
                      time: '09:00',
                      intervalWeeks: 2,
                      weekday: 'friday',
                      anchorDate: null,
                    });
                  }}
                  className="px-4 py-2 border rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reminder settings modal (per babysitter) */}
      {editingReminders && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <MdNotifications className="text-primary-600 dark:text-primary-400" />
                {t('settings.reminders.title') || 'Podsetnici'} — {editingReminders.name || editingReminders.email}
              </h3>
              <button
                onClick={() => setEditingReminders(null)}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('settings.reminders.description') || 'Konfigurišite podsetnike za plaćanja za ovu bebisiterku.'}
            </p>
            {reminderSettingsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={reminderSettings.reminder7d}
                      onChange={(e) => setReminderSettings({ ...reminderSettings, reminder7d: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.notifications.reminder7d') || '7 dana pre dospeća'}</span>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={reminderSettings.reminder3d}
                      onChange={(e) => setReminderSettings({ ...reminderSettings, reminder3d: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.notifications.reminder3d') || '3 dana pre dospeća'}</span>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={reminderSettings.reminder0d}
                      onChange={(e) => setReminderSettings({ ...reminderSettings, reminder0d: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.notifications.reminder0d') || 'Na dan dospeća'}</span>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={reminderSettings.reminderPlus3d}
                      onChange={(e) => setReminderSettings({ ...reminderSettings, reminderPlus3d: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.notifications.reminderPlus3d') || '3 dana posle dospeća'}</span>
                  </label>
                </div>
                <div className="grid grid-cols-1 gap-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={reminderSettings.emailEnabled}
                      onChange={(e) => setReminderSettings({ ...reminderSettings, emailEnabled: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold block">{t('settings.notifications.emailLabel') || 'Email'}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.notifications.emailDescription') || 'Slanje podsetnika na email'}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-white/60 dark:border-gray-700/60 rounded-xl bg-white/70 dark:bg-gray-900/60 hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary-600"
                      checked={reminderSettings.notificationEnabled}
                      onChange={(e) => setReminderSettings({ ...reminderSettings, notificationEnabled: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold block">{t('settings.notifications.inAppLabel') || 'Obaveštenja u aplikaciji'}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.notifications.inAppDescription') || 'Prikaz obaveštenja u aplikaciji'}</span>
                    </span>
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={async () => {
                      try {
                        const friendId = editingReminders.friendshipId || editingReminders.id;
                        await updatePaymentReminderSettingsForFriend(friendId, reminderSettings);
                        showToast(t('settings.notifications.saved') || 'Podešavanja su sačuvana', 'success');
                        setEditingReminders(null);
                      } catch (err) {
                        console.error('Save reminder settings error:', err);
                        showToast(t('settings.notifications.error') || 'Greška pri čuvanju podešavanja', 'error');
                      }
                    }}
                    className="btn-primary flex-1"
                  >
                    {t('common.save') || 'Sačuvaj'}
                  </button>
                  <button
                    onClick={() => setEditingReminders(null)}
                    className="px-4 py-2 border rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {t('common.cancel') || 'Otkaži'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full-Time Work Modal */}
      {editingFullTimeWork && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('friends.fullTimeWork') || 'Puno radno vreme'}
              </h3>
              <button
                onClick={() => {
                  setEditingFullTimeWork(null);
                  setFullTimeWork({
                    enabled: false,
                    startTime: '09:00',
                    endTime: '17:00',
                    days: []
                  });
                }}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Enable/Disable */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fullTimeWork.enabled}
                    onChange={(e) => setFullTimeWork({ ...fullTimeWork, enabled: e.target.checked })}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('friends.enableFullTimeWork') || 'Omogući automatski unos radnog vremena'}
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                  {t('friends.fullTimeWorkHint') || 'Automatski će se uneti radno vreme za izabrane dane nakon završetka radnog vremena'}
                </p>
              </div>

              {fullTimeWork.enabled && (
                <>
                  {/* Working Hours */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        {t('friends.startTime') || 'Početak'}
                      </label>
                      <input
                        type="time"
                        value={fullTimeWork.startTime}
                        onChange={(e) => setFullTimeWork({ ...fullTimeWork, startTime: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        {t('friends.endTime') || 'Kraj'}
                      </label>
                      <input
                        type="time"
                        value={fullTimeWork.endTime}
                        onChange={(e) => setFullTimeWork({ ...fullTimeWork, endTime: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Working Days */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('friends.workingDays') || 'Radni dani'}
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {[
                        { value: 0, label: t('calendar.sunday') || 'N' },
                        { value: 1, label: t('calendar.monday') || 'P' },
                        { value: 2, label: t('calendar.tuesday') || 'U' },
                        { value: 3, label: t('calendar.wednesday') || 'S' },
                        { value: 4, label: t('calendar.thursday') || 'Č' },
                        { value: 5, label: t('calendar.friday') || 'P' },
                        { value: 6, label: t('calendar.saturday') || 'S' }
                      ].map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => {
                            const newDays = [...(fullTimeWork.days || [])];
                            if (newDays.includes(day.value)) {
                              setFullTimeWork({ ...fullTimeWork, days: newDays.filter(d => d !== day.value) });
                            } else {
                              setFullTimeWork({ ...fullTimeWork, days: [...newDays, day.value] });
                            }
                          }}
                          className={`px-2 py-2 text-sm rounded-xl border transition-colors ${
                            (fullTimeWork.days || []).includes(day.value)
                              ? 'bg-green-100 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-300'
                              : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    try {
                      if (fullTimeWork.enabled && (!fullTimeWork.startTime || !fullTimeWork.endTime || !fullTimeWork.days || fullTimeWork.days.length === 0)) {
                        showToast(t('friends.fillAllFields') || 'Popunite sva polja', 'error');
                        return;
                      }

                      const targetFriendshipId = editingFullTimeWork?.friendshipId || editingFullTimeWork?.id;
                      if (!targetFriendshipId) {
                        showToast(t('friends.scheduleUpdateError') || 'Nedostaje identifikator prijatelja', 'error');
                        return;
                      }

                      await api.put(`/friends/${targetFriendshipId}/full-time-work`, fullTimeWork);
                      const updatedFriends = friends.map(f => 
                        f.friendshipId === targetFriendshipId 
                          ? { ...f, fullTimeWork: fullTimeWork }
                          : f
                      );
                      setFriends(updatedFriends);
                      setEditingFullTimeWork(null);
                      showToast(t('friends.fullTimeWorkUpdated') || 'Puno radno vreme je ažurirano', 'success');
                    } catch (error) {
                      console.error('Error updating full-time work:', error);
                      showToast(t('friends.fullTimeWorkError') || 'Greška pri ažuriranju punog radnog vremena: ' + (error.response?.data?.message || error.message), 'error');
                    }
                  }}
                  className="btn-primary flex-1"
                >
                  {t('common.save') || 'Sačuvaj'}
                </button>
                <button
                  onClick={() => {
                    setEditingFullTimeWork(null);
                    setFullTimeWork({
                      enabled: false,
                      startTime: '09:00',
                      endTime: '17:00',
                      days: []
                    });
                  }}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('friends.removeConfirm') || 'Potvrda uklanjanja'}
        message={t('friends.removeConfirmMessage') || 'Da li ste sigurni da želite da uklonite ovog prijatelja?'}
        onConfirm={confirmRemoveFriend}
        onCancel={() => setDeleteConfirm({ isOpen: false, friendId: null })}
      />
      {/* Share Calendar Dialog with Options */}
      {shareConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('friends.shareCalendarConfirm') || 'Podeli sa prijateljem'}
              </h3>
              <button
                onClick={() => {
                  setShareConfirm({ isOpen: false, friendId: null });
                  setShareSettings({ calendar: true, payments: false, babysitters: false, calendarWrite: false });
                }}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('friends.selectWhatToShare') || 'Izaberite šta želite da podelite:'}
              </p>
              
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareSettings.calendar}
                    onChange={(e) => setShareSettings({
                      ...shareSettings,
                      calendar: e.target.checked,
                      calendarWrite: e.target.checked ? shareSettings.calendarWrite : false
                    })}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('friends.shareCalendar') || 'Kalendar'}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('friends.shareCalendarDesc') || 'Podeli sve unose na kalendaru'}
                    </p>
                  </div>
                </label>

                <label className={`flex items-center gap-3 ${shareSettings.calendar ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                  <input
                    type="checkbox"
                    checked={shareSettings.calendar && shareSettings.calendarWrite}
                    disabled={!shareSettings.calendar}
                    onChange={(e) => setShareSettings({ ...shareSettings, calendarWrite: e.target.checked })}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Može da menja kalendar
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Dozvoli dodavanje, izmenu i brisanje unosa u podeljenom kalendaru
                    </p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareSettings.payments}
                    onChange={(e) => setShareSettings({ ...shareSettings, payments: e.target.checked })}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('friends.sharePayments') || 'Plaćanja'}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('friends.sharePaymentsDesc') || 'Podeli informacije o plaćanjima'}
                    </p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareSettings.babysitters}
                    onChange={(e) => setShareSettings({ ...shareSettings, babysitters: e.target.checked })}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('friends.shareBabysitters') || 'Bebisiterke'}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('friends.shareBabysittersDesc') || 'Podeli veze sa bebisiterkama'}
                    </p>
                  </div>
                </label>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 mt-6">
              {shareConfirm.friendId && hasAnyShared(friends.find(f => f.id === shareConfirm.friendId)) && (
                <button
                  type="button"
                  onClick={handleUnshareAll}
                  className="w-full px-4 py-2 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm"
                >
                  {t('friends.unshareAll') || 'Ukloni deljenje (raskini sve)'}
                </button>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShareConfirm({ isOpen: false, friendId: null });
                    setShareSettings({ calendar: true, payments: false, babysitters: false, calendarWrite: false });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
                <button
                  onClick={confirmShareCalendar}
                  disabled={!shareSettings.calendar && !shareSettings.payments && !shareSettings.babysitters}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('friends.share') || 'Podeli'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Friend Dialog - kada korisnik nije na Nannity (add by email) ili za ručno dodate (Pozovi prijatelja) */}
      {inviteFriendDialog.isOpen && inviteFriendDialog.friend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {inviteFriendDialog.friend.fromAddByEmail
                  ? (t('friends.inviteToNannity') || 'Pozovi na Nannity')
                  : (t('friends.inviteFriend') || 'Pozovi prijatelja')}
              </h3>
              <button
                onClick={() => setInviteFriendDialog({ isOpen: false, friend: null })}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {inviteFriendDialog.friend.fromAddByEmail
                  ? (t('friends.userNotOnNannityInvite') || 'Korisnik sa ovim emailom nije na Nannity. Želite da mu pošaljete pozivnicu da se pridruži platformi?')
                  : (t('friends.inviteFriendMessage') || 'Želite li da pozovete {name} da se pridruži Nannity platformi?').replace('{name}', inviteFriendDialog.friend.name || inviteFriendDialog.friend.email)}
              </p>
              {inviteFriendDialog.friend.email && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {inviteFriendDialog.friend.email}
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('friends.inviteFriendBenefits') || 'Prijatelj će dobiti email sa linkom za registraciju i objašnjenjem prednosti platforme.'}
              </p>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleInviteFriend}
                  className="btn-primary flex-1"
                >
                  {t('friends.sendInvitation') || 'Pošalji pozivnicu'}
                </button>
                <button
                  onClick={() => setInviteFriendDialog({ isOpen: false, friend: null })}
                  className="px-4 py-2 border rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Friend Modal */}
      {editingFriend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full my-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('friends.editFriend') || 'Izmeni prijatelja'}
              </h3>
              <button
                onClick={() => {
                  setEditingFriend(null);
                  setEditingFriendRates(false);
                  setEditFriendForm({ name: '', email: '', phone: '', hourlyRate: '', flexibleRates: [] });
                }}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            {/* Relationship Type Display */}
            <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                {t('friends.relationshipType') || 'Tip veze'}:{' '}
                {editingFriend.relationshipType === 'babysitter' 
                  ? (user?.role === 'babysitter' 
                      ? (t('friends.family') || 'Porodica sa kojom sarađujem')
                      : (t('friends.babysitter') || 'Bebisiterka'))
                  : (user?.role === 'babysitter'
                      ? (t('friends.babysitter') || 'Bebisiterka')
                      : (t('friends.family') || 'Porodica'))
                }
              </span>
            </div>

            {!editingFriendRates ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('friends.fullName') || 'Ime i prezime'}
                  </label>
                  <input
                    type="text"
                    value={editFriendForm.name}
                    onChange={(e) => setEditFriendForm({ ...editFriendForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder={t('friends.fullNamePlaceholder') || 'Unesite ime i prezime'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('friends.friendEmail') || 'Email'}
                  </label>
                  <input
                    type="email"
                    value={editFriendForm.email}
                    onChange={(e) => setEditFriendForm({ ...editFriendForm, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder={t('friends.emailPlaceholder') || 'Unesite email prijatelja'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('friends.phone') || 'Telefon'}
                  </label>
                  <input
                    type="tel"
                    value={editFriendForm.phone}
                    onChange={(e) => setEditFriendForm({ ...editFriendForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder={t('friends.phonePlaceholder') || 'Unesite telefon'}
                  />
                </div>

                {editingFriend.relationshipType === 'babysitter' && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('friends.hourlyRate') || 'Satnica'}: {editFriendForm.hourlyRate || editingFriend.hourlyRate || 0} RSD/h
                      </span>
                      <button
                        onClick={() => setEditingFriendRates(true)}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {t('friends.editRates') || 'Uredi satnicu i cene'}
                      </button>
                    </div>
                    {Array.isArray(editFriendForm.flexibleRates) && editFriendForm.flexibleRates.length > 0 && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {t('friends.flexibleRatesConfigured') || 'Fleksibilne cene su podešene'}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleSaveEditFriend}
                    className="btn-primary flex-1"
                  >
                    {t('common.save') || 'Sačuvaj'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingFriend(null);
                      setEditingFriendRates(false);
                      setEditFriendForm({ name: '', email: '', phone: '', hourlyRate: '', flexibleRates: [] });
                    }}
                    className="px-4 py-2 border rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {t('common.cancel') || 'Otkaži'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">
                    {t('friends.editRates') || 'Uredi satnicu i cene'}
                  </h4>
                  <button
                    onClick={() => setEditingFriendRates(false)}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:underline"
                  >
                    {t('common.back') || 'Nazad'}
                  </button>
                </div>

                {/* Hourly Rate */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('friends.hourlyRate') || 'Satnica (RSD/h)'}
                  </label>
                  <input
                    type="number"
                    value={editFriendForm.hourlyRate}
                    onChange={(e) => setEditFriendForm({ ...editFriendForm, hourlyRate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder={t('friends.enterHourlyRate') || 'Unesite satnicu'}
                  />
                </div>

                {/* Flexible Rates - Same as in Add Friend */}
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('calendar.flexibleRates') || 'Fleksibilne cene (RSD/h)'}
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setEditFriendForm({
                          ...editFriendForm,
                          flexibleRates: [
                            ...(Array.isArray(editFriendForm.flexibleRates) ? editFriendForm.flexibleRates : []),
                            {
                              days: [1, 2, 3, 4, 5],
                              startTime: '09:00',
                              endTime: '17:00',
                              rate: parseFloat(editFriendForm.hourlyRate) || 800
                            }
                          ]
                        });
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
                    >
                      <MdAdd className="text-sm" />
                      {t('common.add') || 'Dodaj'}
                    </button>
                  </div>
                  
                  {!Array.isArray(editFriendForm.flexibleRates) || editFriendForm.flexibleRates.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                      {t('friends.noRates') || 'Nema definisanih cena. Kliknite "Dodaj" da dodate.'}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {editFriendForm.flexibleRates.map((rate, index) => (
                        <div key={index} className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              {t('friends.ratePeriod') || 'Period'} {index + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                if (!Array.isArray(editFriendForm.flexibleRates)) return;
                                setEditFriendForm({
                                  ...editFriendForm,
                                  flexibleRates: editFriendForm.flexibleRates.filter((_, i) => i !== index)
                                });
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <MdClose className="text-sm" />
                            </button>
                          </div>
                          
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                {t('friends.days') || 'Dani'}
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'].map((day, dayIndex) => {
                                  const actualDayIndex = dayIndex < 6 ? dayIndex + 1 : 0;
                                  return (
                                    <label key={dayIndex} className="flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={rate.days.includes(actualDayIndex)}
                                        onChange={(e) => {
                                          if (!Array.isArray(editFriendForm.flexibleRates)) return;
                                          const newRates = [...editFriendForm.flexibleRates];
                                          if (e.target.checked) {
                                            newRates[index].days = [...newRates[index].days, actualDayIndex].sort();
                                          } else {
                                            newRates[index].days = newRates[index].days.filter(d => d !== actualDayIndex);
                                          }
                                          setEditFriendForm({ ...editFriendForm, flexibleRates: newRates });
                                        }}
                                        className="w-3 h-3"
                                      />
                                      <span className="text-xs">{day}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {t('calendar.startTime') || 'Početak'}
                                </label>
                                <input
                                  type="time"
                                  value={rate.startTime}
                                  onChange={(e) => {
                                    if (!Array.isArray(editFriendForm.flexibleRates)) return;
                                    const newRates = [...editFriendForm.flexibleRates];
                                    newRates[index].startTime = e.target.value;
                                    setEditFriendForm({ ...editFriendForm, flexibleRates: newRates });
                                  }}
                                  className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {t('calendar.endTime') || 'Kraj'}
                                </label>
                                <input
                                  type="time"
                                  value={rate.endTime}
                                  onChange={(e) => {
                                    if (!Array.isArray(editFriendForm.flexibleRates)) return;
                                    const newRates = [...editFriendForm.flexibleRates];
                                    newRates[index].endTime = e.target.value;
                                    setEditFriendForm({ ...editFriendForm, flexibleRates: newRates });
                                  }}
                                  className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                {t('friends.rate') || 'Cena (RSD/h)'}
                              </label>
                              <input
                                type="number"
                                value={rate.rate}
                                onChange={(e) => {
                                  if (!Array.isArray(editFriendForm.flexibleRates)) return;
                                  const newRates = [...editFriendForm.flexibleRates];
                                  newRates[index].rate = parseFloat(e.target.value) || 0;
                                  setEditFriendForm({ ...editFriendForm, flexibleRates: newRates });
                                }}
                                className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSaveEditFriend}
                    className="btn-primary flex-1"
                  >
                    {t('common.save') || 'Sačuvaj'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingFriendRates(false);
                    }}
                    className="px-4 py-2 border rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {t('common.cancel') || 'Otkaži'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
